import { defModule, Task, type TaskMessage, type UpdateResult } from '@st/redux'
import { Effect } from 'effect'
import { OrganizationMembership } from '@st/sdk'
import { match } from '@st/pattern'
import { routes, toPath, type AppRoute } from '~/features/routes'
import { SdkClient, Routing } from '~/effects'
import { observeAuth, observeLocation, runSdkRequest } from '~/features/module-runtime'
import { organizationSignUpFlow } from './organization-sign-up-flow'

type BaseState = {
  route: AppRoute | null
}

export type OrganizationMembershipsState =
  | ({ status: 'idle' } & BaseState)
  | ({ status: 'loading' } & BaseState)
  | ({ status: 'loaded'; memberships: OrganizationMembership[] } & BaseState)
  | ({ status: 'failed' } & BaseState)

type MembershipsLoaded = {
  type: 'loaded'
  memberships: OrganizationMembership[]
}

type MembershipsLoadFailed = {
  type: 'failed'
}

const loadMemberships = Task.define(() =>
  Effect.gen(function* () {
    const sdk = yield* SdkClient
    const result = yield* runSdkRequest(
      sdk.send({ type: 'organizations/getMyOrganizationMemberships' })
    )
    if (!result.ok) return { type: 'failed' } satisfies MembershipsLoadFailed
    return {
      type: 'loaded',
      memberships: result.response.memberships
    } satisfies MembershipsLoaded
  })
)

type MembershipRouteChanged = {
  type: 'routeChanged'
  route: AppRoute | null
}

const observeMembershipRoute = observeLocation(
  ({ pathname }): MembershipRouteChanged => ({
    type: 'routeChanged',
    route: routes.match(pathname) ?? null
  })
)

type MembershipAuthChanged = { type: 'authLoggedIn' } | { type: 'authLoggedOut' }

const observeMembershipAuth = observeAuth((event): MembershipAuthChanged | null => {
  switch (event.type) {
    case 'loggedIn':
      return { type: 'authLoggedIn' }
    case 'loggedOut':
      return { type: 'authLoggedOut' }
    case 'loginFailed':
      return null
  }
})

export type OrganizationMembershipsMessage =
  | TaskMessage<typeof observeMembershipRoute>
  | TaskMessage<typeof observeMembershipAuth>
  | TaskMessage<typeof loadMemberships>
  | { type: 'selectOrganization'; organizationSlug: string }
  | { type: 'redirectToSingleOrg'; route: AppRoute }
  | { type: 'retryLoad' }

export const organizationMembershipsModule = defModule<{
  state: OrganizationMembershipsState
  message: OrganizationMembershipsMessage
}>({
  init: () => ({ status: 'idle', route: null }),
  tasks: organizationMembershipsTasks,
  update: updateOrganizationMemberships
})

function organizationMembershipsTasks(state: OrganizationMembershipsState) {
  const observations = [observeMembershipAuth(), observeMembershipRoute()]
  if (state.status == 'loading') {
    return [...observations, loadMemberships()]
  }
  return observations
}

/**
 * Authentication owns one membership-loading lifetime:
 *
 * idle -> loading -> loaded / failed
 *
 * Logging out removes the loading Task. The runtime revokes its output before
 * interruption, so load outcomes contain no user, request, or session identity.
 */
function updateOrganizationMemberships(
  state: OrganizationMembershipsState,
  message: OrganizationMembershipsMessage
): UpdateResult<OrganizationMembershipsState, OrganizationMembershipsMessage> {
  return match({ state, message })
    .returnType<UpdateResult<OrganizationMembershipsState, OrganizationMembershipsMessage>>()
    .with({ message: { type: 'routeChanged' } }, ({ state, message }) => {
      if (sameRoute(state.route, message.route)) return state
      return maybeRedirectSingleOrg({ ...state, route: message.route })
    })
    .with({ message: { type: 'authLoggedIn' } }, ({ state }) => {
      if (state.status == 'loading' || state.status == 'loaded') return state
      return { status: 'loading', route: state.route }
    })
    .with({ message: { type: 'authLoggedOut' } }, ({ state }) => ({
      status: 'idle',
      route: state.route
    }))
    .with({ state: { status: 'loading' }, message: { type: 'loaded' } }, ({ state, message }) =>
      maybeRedirectSingleOrg({
        status: 'loaded',
        memberships: message.memberships,
        route: state.route
      })
    )
    .with({ state: { status: 'loading' }, message: { type: 'failed' } }, ({ state }) => ({
      status: 'failed',
      route: state.route
    }))
    .with({ state: { status: 'failed' }, message: { type: 'retryLoad' } }, ({ state }) => ({
      status: 'loading',
      route: state.route
    }))
    .with({ message: { type: 'selectOrganization' } }, ({ state, message }) =>
      navigateToOrganizationRoute(
        state,
        organizationRouteForRoute(state.route, message.organizationSlug) ?? {
          name: 'folder_list',
          organizationSlug: message.organizationSlug
        }
      )
    )
    .with({ message: { type: 'redirectToSingleOrg' } }, ({ state, message }) =>
      navigateToOrganizationRoute(state, message.route)
    )
    .otherwise(({ state, message }) => unexpectedMessage(state, message))
}

function maybeRedirectSingleOrg(state: OrganizationMembershipsState) {
  if (state.status != 'loaded' || state.memberships.length != 1) {
    return state
  }

  const slug = state.memberships[0].organization.slug
  const route = organizationRouteForRoute(state.route, slug)

  if (!route) {
    return state
  }

  return [
    state,
    Effect.succeed({
      type: 'redirectToSingleOrg',
      route
    } as const)
  ] as const
}

function organizationRouteForRoute(
  route: AppRoute | null,
  organizationSlug: string
): AppRoute | null {
  switch (route?.name) {
    case '/':
    case 'login':
      return { name: 'folder_list', organizationSlug }
    case 'sign_up':
      return organizationSignUpFlow(route.flow).completionRoute(organizationSlug)
    case 'logged_out_pricing':
      return { name: 'logged_in_pricing', organizationSlug }
    default:
      return null
  }
}

function navigateToOrganizationRoute(state: OrganizationMembershipsState, route: AppRoute) {
  return [
    state,
    Effect.gen(function* () {
      const routing = yield* Routing
      yield* routing.navigate(route)
      return null
    })
  ] as const
}

function sameRoute(left: AppRoute | null, right: AppRoute | null) {
  if (left == null || right == null) {
    return left == right
  }

  return left.name == right.name && toPath(left) == toPath(right)
}

function unexpectedMessage(
  state: OrganizationMembershipsState,
  message: OrganizationMembershipsMessage
): never {
  throw new Error(
    `Unexpected ${message.type} message in ${state.status} organization memberships state`
  )
}
